import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";

interface ConfirmDeleteDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
  itemName?: string;
  loading?: boolean;
  variant?: "trash" | "permanent";
}

export function ConfirmDeleteDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  itemName,
  loading = false,
  variant = "trash",
}: ConfirmDeleteDialogProps) {
  const { t } = useTranslation();

  const finalTitle = title || (variant === "permanent"
    ? t("common.confirmPermanentDelete")
    : t("common.confirmDelete"));

  const finalDescription = description || (variant === "permanent"
    ? t("common.confirmPermanentDeleteMessage", { name: itemName || "" })
    : t("common.confirmDeleteMessage", { name: itemName || "" }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
              variant === "permanent" ? "bg-red-100" : "bg-amber-100"
            }`}>
              {variant === "permanent"
                ? <AlertTriangle className="h-5 w-5 text-red-600" />
                : <Trash2 className="h-5 w-5 text-amber-600" />
              }
            </div>
            <DialogTitle>{finalTitle}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {finalDescription}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? t("common.loading") : (variant === "permanent"
              ? t("common.confirmPermanentDelete")
              : t("common.delete")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
